import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { FileText, Download, Calendar, Eye, FolderOpen, Shield, Award } from "lucide-react";

function Documents() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Sample documents - replace with actual data
  const documents = [
    {
      id: 1,
      name: "MY NEW NORMALS Project Proposal",
      link: "uploads/MY NEW NORMALS Project PROPOSAL.pdf",
      date: "2021-02-01",
      type: "Project Proposal",
      size: "2.4 MB"
    },
    {
      id: 2,
      name: "Sanyuka Nange Project Report",
      link: "uploads/Sanyuka nange project report.pdf", 
      date: "2021-03-11",
      type: "Project Report",
      size: "1.8 MB"
    },
    {
      id: 3,
      name: "Vocational Skills Training Proposal",
      link: "uploads/proposal VTC.pdf",
      date: "2021-04-04", 
      type: "Training Proposal",
      size: "3.2 MB"
    }
  ];

  const getDocumentIcon = (type) => {
    switch(type) {
      case "Project Proposal": return Award;
      case "Project Report": return FileText;
      case "Training Proposal": return Shield;
      default: return FileText;
    }
  };

  const getDocumentColor = (type) => {
    switch(type) {
      case "Project Proposal": return "from-blue-500 to-cyan-500";
      case "Project Report": return "from-green-500 to-emerald-500";
      case "Training Proposal": return "from-purple-500 to-pink-500";
      default: return "from-gray-500 to-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />
      
      {/* Hero Image Section */}
      <div className="relative pt-8">
        <div className="w-full h-64 md:h-80 overflow-hidden">
          <img 
            src="img/main.jpg" 
            className="w-full h-full object-cover object-center"
            alt="Documents and Resources"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <FolderOpen className="w-4 h-4 mr-2" />
              Project Documentation
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Documents
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Access our project proposals, reports, and documentation
            </p>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="documents-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['documents-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Project <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Documentation</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive documentation of our projects, proposals, and impact reports
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mx-auto mt-6"></div>
          </div>

          {/* Document Cards - Modern Design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {documents.map((doc, index) => {
              const IconComponent = getDocumentIcon(doc.type);
              const colorClass = getDocumentColor(doc.type);
              
              return (
                <div
                  key={doc.id}
                  id={`doc-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`doc-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                    <div className={`w-16 h-16 bg-gradient-to-r ${colorClass} rounded-2xl flex items-center justify-center mb-6`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="mb-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${colorClass} bg-opacity-10 text-gray-700 mb-3`}>
                        {doc.type}
                      </span>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{doc.name}</h3>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(doc.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="text-sm text-gray-500">{doc.size}</div>
                    </div>
                    
                    <div className="flex gap-3">
                      <a
                        href={doc.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex-1 bg-gradient-to-r ${colorClass} hover:shadow-lg text-white px-4 py-3 rounded-xl font-semibold text-center transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2`}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Traditional Table View - Clean & Modern */}
          <div 
            id="table-view"
            data-animate
            className={`transform transition-all duration-1000 delay-600 ${
              isVisible['table-view'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  <FileText className="w-6 h-6 mr-3" />
                  Document Repository
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">No.</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Document Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Download</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {documents.map((doc, index) => {
                      const IconComponent = getDocumentIcon(doc.type);
                      const colorClass = getDocumentColor(doc.type);
                      
                      return (
                        <tr key={doc.id} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {doc.id}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center">
                              <div className={`w-10 h-10 bg-gradient-to-r ${colorClass} rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
                                <IconComponent className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="font-medium">{doc.name}</div>
                                <div className="text-gray-500 text-xs">{doc.size}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r ${colorClass} bg-opacity-10 text-gray-700`}>
                              {doc.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <a
                              href={doc.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-2 bg-gradient-to-r ${colorClass} text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2" />
                              {new Date(doc.date).toLocaleDateString()}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Empty State Message */}
          {documents.length === 0 && (
            <div className="text-center py-16">
              <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Documents Available</h3>
              <p className="text-gray-500">Check back later for project documentation and reports.</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Documents;